"use client";

import { Trans } from "@lingui/react/macro";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

type Props = {
  text: string;
  maxLines?: number;
};

const DEFAULT_MAX_LINES = 1;

export default function ReadMoreText({
  text,
  maxLines = DEFAULT_MAX_LINES,
}: Props) {
  const textId = useId();
  const textRef = useRef<HTMLParagraphElement>(null);
  // 「もっと読む」「閉じる」ボタンを表示するかどうか
  const [showToggleButton, setShowToggleButton] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: text と maxLines の DOM 反映後にも paint 前の再計測が必要。
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const performCheckOverflow = () => {
      // sticky 切り替え中の display:none は計測不能。直前の状態を保ち、再表示時に測り直す。
      if (el.clientWidth === 0) return;
      if (expanded) {
        // 展開時は常に「閉じる」ボタンを表示
        setShowToggleButton(true);
        return;
      }
      // scrollHeight と clientHeight の比較でオーバーフロー（省略されているか）を判定
      const hasOverflow = el.scrollHeight > el.clientHeight;
      setShowToggleButton(hasOverflow);
    };

    performCheckOverflow();
    const observer = new ResizeObserver(performCheckOverflow);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [text, expanded, maxLines]);

  const pStyle: React.CSSProperties = !expanded
    ? {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: maxLines,
        overflow: "hidden",
      }
    : {};

  return (
    <div className="flex items-start gap-2">
      <p
        id={textId}
        ref={textRef}
        className="min-w-0 max-w-full whitespace-pre-wrap text-sm text-muted-foreground"
        style={pStyle}
      >
        {text}
      </p>
      {showToggleButton && (
        <Button
          variant="link"
          className="h-5 shrink-0 px-0 text-xs text-primary hover:text-primary/80 hover:no-underline"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-controls={textId}
        >
          {expanded ? <Trans>Read less</Trans> : <Trans>Read more</Trans>}
        </Button>
      )}
    </div>
  );
}
